import { useSession } from '../../api/session.jsx';
import { PleinEcran } from '../../components/ui/PleinEcran.jsx';
import { tr, trf } from '../../lib/i18n';
import { printDoc } from '../../lib/print';
import { C } from '../../theme/palette';

/** Le jour d'aujourd'hui, à la suisse : « 31.10.2026 ». */
function aujourdhui() {
  const d = new Date();
  const deux = (n) => String(n).padStart(2, '0');
  return deux(d.getDate()) + '.' + deux(d.getMonth() + 1) + '.' + d.getFullYear();
}

/**
 * Les six codes de secours, en deux colonnes, comme sur la maquette.
 *
 * @param {object} props propriétés
 * @param {string[]} props.codes codes « 4821-7730 »
 * @param {boolean} [props.grand] en grand, pour la feuille imprimée
 * @returns {JSX.Element} la grille
 */
export function ListeCodes({ codes, grand = false }) {
  return (
    <div
      aria-label={tr('Mes codes de secours')}
      style={{
        background: grand ? '#fff' : '#F1F5F9',
        border: grand ? '1.5px solid #CBD5E1' : 'none',
        borderRadius: 10,
        padding: grand ? '14px 18px' : '10px 12px',
        fontFamily: 'monospace',
        fontSize: grand ? 20 : 14,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: grand ? '12px 24px' : '4px 18px',
        marginTop: 8,
        color: grand ? '#0F1A2E' : C.amber,
      }}
    >
      {codes.map((code) => (
        <span key={code}>
          {grand && <span style={{ marginRight: 10, color: '#94A3B8' }}>☐</span>}
          {code}
        </span>
      ))}
    </div>
  );
}

/**
 * La feuille A4 des codes de secours, à imprimer et garder loin du téléphone.
 *
 * <p>Posée par-dessus tout l'écran ; à l'impression, seule la feuille sort :
 * le reste de l'application est caché.
 *
 * @param {object} props propriétés
 * @param {string[]} props.codes codes à imprimer
 * @param {Function} props.onFerme referme la feuille
 * @returns {JSX.Element} la feuille
 */
export function FeuilleCodesSecours({ codes, onFerme }) {
  const { session } = useSession();
  const bouton = {
    border: 'none',
    borderRadius: 8,
    padding: '8px 14px',
    fontSize: 12,
    fontWeight: 800,
    cursor: 'pointer',
  };
  return (
    <PleinEcran>
      <div
        className="codes-secours-impression"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9500,
          overflowY: 'auto',
          background: '#4a4e57',
        }}
      >
        <style>
          {
            '@media print{@page{size:A4;margin:0}html,body{background:#fff!important}body>*:not(.codes-secours-impression){display:none!important}.codes-secours-impression{position:static!important;overflow:visible!important;background:#fff!important}.no-print{display:none!important}.a4-wrap{padding:0!important}.a4-sheet{box-shadow:none!important;margin:0!important;width:210mm!important;max-width:210mm!important;min-height:auto!important;padding:18mm!important}}'
          }
        </style>
        <div
          className="no-print"
          style={{
            background: C.navy,
            color: '#fff',
            padding: 'calc(var(--st-haut, 0px) + 12px) 14px 12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
          }}
        >
          <button
            type="button"
            onClick={onFerme}
            style={{ ...bouton, background: 'none', color: '#fff', fontSize: 14, padding: 0 }}
          >
            {tr('‹ Retour')}
          </button>
          <button
            type="button"
            onClick={printDoc}
            style={{ ...bouton, background: C.green, color: '#fff' }}
          >
            {tr('🖨 Imprimer')}
          </button>
        </div>
        <div
          className="a4-wrap"
          style={{ padding: '18px 12px', display: 'flex', justifyContent: 'center' }}
        >
          <div
            className="a4-sheet"
            style={{
              width: '100%',
              maxWidth: '210mm',
              minHeight: '297mm',
              background: '#fff',
              color: '#0F1A2E',
              boxSizing: 'border-box',
              padding: '18mm 16mm',
              boxShadow: '0 6px 24px rgba(0,0,0,.35)',
              fontFamily: "'Helvetica Neue',Arial,sans-serif",
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>
              🔐 {tr('Mes codes de secours')}
            </div>
            <div style={{ fontSize: 12, color: '#5a6270', marginBottom: 18 }}>
              {session?.email ? session.email + ' · ' : ''}
              {trf('Créés le {0}', aujourdhui())}
            </div>
            <ListeCodes codes={codes} grand />
            <ul style={{ fontSize: 12.5, lineHeight: 1.7, marginTop: 22, paddingLeft: 18 }}>
              <li>
                {tr(
                  'À la connexion, à l’étape du code, touche « Utiliser un code de secours » et tape l’un de ces codes.',
                )}
              </li>
              <li>{tr('Chaque code ne sert qu’une fois.')}</li>
              <li>{tr('Garde cette feuille en lieu sûr, loin de ton téléphone.')}</li>
              <li>{tr('Si tu refais de nouveaux codes, cette feuille ne vaut plus rien.')}</li>
            </ul>
          </div>
        </div>
      </div>
    </PleinEcran>
  );
}
